import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { GrDatabase } from 'react-icons/gr';
import axios from 'axios';
import swal from 'sweetalert2';
import WebService from "../../../services/webservice";
import { FaTimes, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import './AppMenu.css';
import AddAppMenu from './AddAppMenu';
import EditAppMenu from './EditAppMenu';

const AppMenu = () => {
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [menuPositionFilter, setMenuPositionFilter] = useState('');
    const [activeTab, setActiveTab] = useState('Parent Menu');
    const [sortField, setSortField] = useState('menuPosition');
    const [sortOrder, setSortOrder] = useState('asc');

    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Modals state
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [menuToEdit, setMenuToEdit] = useState(null);

    // Reset to page 1 if search term, filters, tab or sorting changes
    useEffect(() => {
        setPage(1);
    }, [searchTerm, menuPositionFilter, activeTab, sortField, sortOrder]);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const healthIcons = [
        "fa-solid fa-heartbeat", "fa-solid fa-hospital", "fa-solid fa-user-md", "fa-solid fa-stethoscope",
        "fa-solid fa-pills", "fa-solid fa-prescription-bottle-alt", "fa-solid fa-ambulance", "fa-solid fa-plus-square",
        "fa-solid fa-h-square", "fa-solid fa-briefcase-medical", "fa-solid fa-notes-medical", "fa-solid fa-capsules",
        "fa-solid fa-syringe", "fa-solid fa-vial", "fa-solid fa-vials", "fa-solid fa-pump-medical",
        "fa-solid fa-laptop-medical", "fa-solid fa-book-medical", "fa-solid fa-file-medical", "fa-solid fa-file-medical-alt",
        "fa-solid fa-clinic-medical", "fa-solid fa-comment-medical", "fa-solid fa-disease", "fa-solid fa-dna",
        "fa-solid fa-crutch", "fa-solid fa-x-ray", "fa-solid fa-virus", "fa-solid fa-viruses",
        "fa-solid fa-virus-slash", "fa-solid fa-tooth", "fa-solid fa-teeth", "fa-solid fa-teeth-open",
        "fa-solid fa-smoking", "fa-solid fa-smoking-ban", "fa-solid fa-skull-crossbones", "fa-solid fa-prescription",
        "fa-solid fa-lungs", "fa-solid fa-lungs-virus", "fa-solid fa-id-card-clip", "fa-solid fa-hand-holding-medical",
        "fa-solid fa-head-side-mask", "fa-solid fa-head-side-virus", "fa-solid fa-eye", "fa-solid fa-eye-dropper",
        "fa-solid fa-bacteria", "fa-solid fa-bacterium", "fa-solid fa-band-aid", "fa-solid fa-bone",
        "fa-solid fa-brain", "fa-solid fa-calendar-plus", "fa-solid fa-chart-line", "fa-solid fa-clipboard-list",
        "fa-solid fa-robot", "fa-solid fa-bell", "fa-solid fa-question-circle", "fa-solid fa-tachometer-alt",
        "fa-solid fa-user", "fa-solid fa-users", "fa-solid fa-chart-pie", "fa-solid fa-list",
         "fa-solid fa-cog", "fa-solid fa-wrench", "fa-solid fa-sync", "fa-solid fa-sliders" ,
        "fa-solid fa-sign-out-alt", "fa-solid fa-shield-alt", "fa-solid fa-lock", "fa-solid fa-unlock",
        "fa-solid fa-key", "fa-solid fa-id-badge", "fa-solid fa-home", "fa-solid fa-globe",
        "fa-solid fa-folder", "fa-solid fa-folder-open", "fa-solid fa-file", "fa-solid fa-file-alt",
        "fa-solid fa-envelope", "fa-solid fa-envelope-open", "fa-solid fa-edit", "fa-solid fa-trash",
        "fa-solid fa-trash-alt", "fa-solid fa-search", "fa-solid fa-plus", "fa-solid fa-minus",
        "fa-solid fa-check", "fa-solid fa-times", "fa-solid fa-arrow-up", "fa-solid fa-arrow-down",
        "fa-solid fa-arrow-left", "fa-solid fa-arrow-right", "fa-solid fa-caret-down", "fa-solid fa-caret-up",
        "fa-solid fa-caret-left", "fa-solid fa-caret-right", "fa-solid fa-angle-down", "fa-solid fa-angle-up",
        "fa-solid fa-angle-left", "fa-solid fa-angle-right", "fa-solid fa-chevron-down", "fa-solid fa-droplet",
        "fa-solid fa-apple-whole", "fa-solid fa-pepper-hot", "fa-solid fa-bread-slice", "fa-solid fa-microscope",
        "fa-solid fa-kit-medical", "fa-solid fa-syringe", "fa-solid fa-heart-pulse", "fa-solid fa-arrow-up-from-bracket",
        "fa-solid fa-music", "fa-solid fa-list-check", "fa-solid fa-list", "fa-solid fa-triangle-exclamation",
        "fa-solid fa-users-gear", "fa-solid fa-cogs", "fa-solid fa-rocket", "fa fa-user", "fa fa-microscope",
        "fa-solid fa-chart-column", "fa-solid fa-female", "fa-solid fa-server", "fa-solid fa-cloud"
    ];

    const iconOptions = healthIcons.map(icon => ({
        value: icon,
        label: icon
    }));

    const formatOptionLabel = ({ value, label }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <i className={value} style={{ fontSize: '18px', marginRight: '10px', width: '25px', textAlign: 'center' }} />
            <span>{label}</span>
        </div>
    );

    const [formData, setFormData] = useState({
        id: '',
        menuName: '',
        menuPosition: '',
        menuKey: '',
        menuIcon: '',
        urlPath: '',
        menuType: 'Parent Menu',
        parentMenu: '',
        allowedActions: []
    });

    const [actionInput, setActionInput] = useState('');

    useEffect(() => {
        fetchMenus();
    }, [page, pageSize, searchTerm, menuPositionFilter, activeTab, sortField, sortOrder]);

    const fetchMenus = async () => {
        try {
            setLoading(true);
            const response = await axios.post(`${WebService}api/app-menus`, {
                page,
                limit: pageSize,
                searchTerm,
                menuPosition: menuPositionFilter,
                menuType: activeTab,
                sortBy: sortField,
                sortOrder: sortOrder
            });
            if (response.data.success) {
                setMenus(response.data.data);
                setTotalCount(response.data.totalRecords || 0);
                setTotalPages(response.data.totalPages || 0);
            }
        } catch (error) {
            console.error('Error fetching app menus:', error);
            swal.fire('Error', 'Failed to fetch app menus.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const renderSortIcon = (field) => {
        if (sortField !== field) return <FaSort />;
        return sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />;
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setMenuPositionFilter('');
        setPage(1);
    };

    const handleDelete = (id) => {
        swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await axios.post(`${WebService}api/delete-app-menu`, { id });
                    if (response.data.success) {
                        swal.fire('Deleted!', 'App menu has been deleted.', 'success');
                        fetchMenus();
                    }
                } catch (error) {
                    console.error('Error deleting app menu:', error);
                    swal.fire('Error', 'Something went wrong while deleting.', 'error');
                }
            }
        });
    };

    const startIndex = (page - 1) * pageSize + 1;
    const endIndex = Math.min(page * pageSize, totalCount);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2>App Menus</h2>
                    <p>Manage application menus and their positions</p>
                </div>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                                type="text" 
                                placeholder="Menu Position" 
                                value={menuPositionFilter}
                                onChange={(e) => setMenuPositionFilter(e.target.value.replace(/[^0-9]/g, ''))}
                                className="form-control"
                                style={{ width: '130px', height: '32px', fontSize: '13px', borderRadius: '20px', backgroundColor: '#f4f6f8', border: '1px solid #e2e8f0', color: '#333' }}
                            />
                            <input 
                                type="text" 
                                placeholder="Search menu ..." 
                                value={searchTerm}
                                onChange={handleSearch}
                                className="form-control"
                                style={{ width: '200px', height: '32px', fontSize: '13px', borderRadius: '20px', backgroundColor: '#f4f6f8', border: '1px solid #e2e8f0', color: '#333' }}
                            />
                            {(searchTerm || menuPositionFilter) && (
                                <button
                                    onClick={clearFilters}
                                    style={{
                                        height: '32px', padding: '0 15px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '20px', 
                                        fontWeight: 600, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer'
                                    }}
                                >
                                    <FaTimes /> Clear
                                </button>
                            )}
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="admin-btn admin-btn-primary"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        + Add Menu
                    </button>
                </div>
            </div>
            <div>
                <div>
                    <div style={{ display: 'none' }}>
                    </div>

                    {/* Tab Switcher */}
                    <div style={{ display: 'flex', gap: '20px', marginTop: '20px', borderBottom: '2px solid #eaeaea' }}>
                        <button
                            onClick={() => setActiveTab('Parent Menu')}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                background: 'none',
                                borderBottom: activeTab === 'Parent Menu' ? '3px solid #1dbeb6ff' : '3px solid transparent',
                                color: activeTab === 'Parent Menu' ? '#1dbeb6ff' : '#6e7687',
                                fontWeight: activeTab === 'Parent Menu' ? '600' : '500',
                                cursor: 'pointer',
                                fontSize: '15px',
                                borderRadius: "0px",
                                marginTop: "-2px"
                            }}
                        >
                            Parent Menus
                        </button>
                        <button
                            onClick={() => setActiveTab('Sub Menu')}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                background: 'none',
                                borderBottom: activeTab === 'Sub Menu' ? '3px solid #1dbeb6ff' : '3px solid transparent',
                                color: activeTab === 'Sub Menu' ? '#1dbeb6ff' : '#6e7687',
                                fontWeight: activeTab === 'Sub Menu' ? '600' : '500',
                                cursor: 'pointer',
                                fontSize: '15px',
                                borderRadius: "0px",
                                marginTop: "-2px"
                            }}
                        >
                            Sub Menus
                        </button>
                    </div>

                    <div style={{ height: '54vh', overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #eaeaea', borderRadius: '8px 8px 0 0', marginTop: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                        <table className="admin-table" style={{ width: '100%', margin: 0, borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                <tr>
                                    <th style={{ padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none' }}>S.NO</th>
                                    <th style={{ padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none' }}>Menu Icon</th>
                                    <th onClick={() => handleSort('menuName')} style={{ cursor: 'pointer', padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none', whiteSpace: 'nowrap' }}>
                                        Menu Name <span style={{ fontSize: '12px', color: '#999', marginLeft: '5px' }}>{renderSortIcon('menuName')}</span>
                                    </th>
                                    {activeTab === 'Sub Menu' &&
                                        <th onClick={() => handleSort('parentMenu')} style={{ cursor: 'pointer', padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none', whiteSpace: 'nowrap' }}>
                                            Parent Menu <span style={{ fontSize: '12px', color: '#999', marginLeft: '5px' }}>{renderSortIcon('parentMenu')}</span>
                                        </th>
                                    }
                                    <th onClick={() => handleSort('menuPosition')} style={{ cursor: 'pointer', padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none', whiteSpace: 'nowrap' }}>
                                        Menu Position <span style={{ fontSize: '12px', color: '#999', marginLeft: '5px' }}>{renderSortIcon('menuPosition')}</span>
                                    </th>
                                    <th onClick={() => handleSort('menuKey')} style={{ cursor: 'pointer', padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none', whiteSpace: 'nowrap' }}>
                                        Menu Key <span style={{ fontSize: '12px', color: '#999', marginLeft: '5px' }}>{renderSortIcon('menuKey')}</span>
                                    </th>
                                    <th style={{ padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr style={{ border: "none" }}>
                                        <td colSpan={activeTab === 'Sub Menu' ? "7" : "6"} className="text-center" style={{ border: "none", height: "53vh" }}>
                                            <Spinner animation="border" variant="primary" />
                                        </td>
                                    </tr>
                                ) : menus.length > 0 ? (
                                    menus.map((menu, index) => (
                                        <tr key={menu._id}>
                                            <td>{(page - 1) * pageSize + index + 1}</td>
                                            <td>
                                                {menu.menuIcon ? <i className={menu.menuIcon} style={{ fontSize: '18px' }} /> : '-'}
                                            </td>
                                            <td>{menu.menuName}</td>
                                            {activeTab === 'Sub Menu' && <td>{menu.parentMenu || '-'}</td>}
                                            <td>{menu.menuPosition}</td>
                                            <td>{menu.menuKey}</td>
                                            <td>
                                                <div className="action-btns">
                                                    <button
                                                        className="action-btn btn-edit"
                                                        onClick={() => {
                                                            setMenuToEdit(menu);
                                                            setShowEditModal(true);
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="action-btn btn-delete"
                                                        onClick={() => handleDelete(menu._id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={activeTab === 'Sub Menu' ? "7" : "6"} style={{ textAlign: 'center', height: '54vh', verticalAlign: 'middle', color: '#777' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                <GrDatabase fontSize={40} color="#bbb" style={{ border: "none" }} />
                                                <span>No data found.</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="pagination-music" style={{ padding: '15px 20px', border: '1px solid #eaeaea', borderTop: 'none', borderRadius: '0 0 8px 8px', backgroundColor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="pagination-info" style={{ color: '#000000ff', fontWeight: '500' }}>
                            Showing {totalCount > 0 ? startIndex : 0} - {endIndex} of {totalCount} records
                        </div>
                        <div className="pagination-buttons" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                                className="page-btn"
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                style={{ padding: '6px 10px', border: 'none', backgroundColor: 'transparent', color: page === 1 ? '#ccc' : '#555', fontWeight: '600', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
                            >
                                Prev
                            </button>

                            {(() => {
                                let pages = [];
                                if (totalPages <= 5) {
                                    for (let i = 1; i <= totalPages; i++) pages.push(i);
                                } else {
                                    if (page <= 3) pages = [1, 2, 3, '...', totalPages];
                                    else if (page >= totalPages - 2) pages = [1, '...', totalPages - 2, totalPages - 1, totalPages];
                                    else pages = [1, '...', page - 1, page, page + 1, '...', totalPages];
                                }
                                return pages.map((p, index) => (
                                    <button
                                        key={index}
                                        className={`page-btn ${page === p ? 'active' : ''}`}
                                        onClick={() => p !== '...' && setPage(p)}
                                        disabled={p === '...'}
                                        style={{
                                            padding: '4px 5px',
                                            border: p === '...' ? 'none' : (page === p ? '1px solid #3b82f6' : '1px solid #eaeaea'),
                                            borderRadius: '8px',
                                            backgroundColor: p === '...' ? 'transparent' : (page === p ? '#3b82f6' : '#fff'),
                                            color: page === p ? '#fff' : '#555',
                                            fontWeight: page === p ? '600' : '500',
                                            cursor: p === '...' ? 'default' : 'pointer',
                                            minWidth: '25px'
                                        }}
                                    >
                                        {p}
                                    </button>
                                ));
                            })()}

                            <button
                                className="page-btn"
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages || totalPages === 0}
                                style={{ padding: '6px 10px', border: 'none', backgroundColor: 'transparent', color: page === totalPages || totalPages === 0 ? '#ccc' : '#555', fontWeight: '600', cursor: page === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer' }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <AddAppMenu 
                show={showAddModal} 
                onHide={() => setShowAddModal(false)} 
                onSuccess={fetchMenus} 
            />

            <EditAppMenu 
                show={showEditModal} 
                onHide={() => {
                    setShowEditModal(false);
                    setMenuToEdit(null);
                }} 
                onSuccess={fetchMenus} 
                menuToEdit={menuToEdit}
            />
        </>
    );
};
export default AppMenu;
